import { Badge, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Download } from 'react-feather';
import { useAuth } from '../../../authentication/useAuth';
import useIsMobile from '../../../hooks/useIsMobile';
import { tokens } from '../../../theme';
import devPreview from '../../../utils/devPreview';
import instance from '../../../utils/imsAxios';

const CHECK_EVERY_MS = 30 * 60 * 1000;
const WAIT_FOR_RESTART_MS = 6 * 60 * 1000;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// For administrators on a site laptop that runs installed releases: shows when a newer release is in S3
// and installs it. Installing restarts IMS, so the page waits for the new version and reloads.
const UpdateButton = () => {
    const { canEdit } = useAuth();
    const isMobile = useIsMobile();
    const allowed = canEdit('system-update');
    const [info, setInfo] = useState(null);
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState('idle'); // idle | installing | done | problem
    const [message, setMessage] = useState('');

    const check = useCallback(async () => {
        if (devPreview('update')) return setInfo({ managed: true, updateAvailable: true, current: 'development', latest: { version: 'preview' } });
        try { setInfo((await instance.get('/system/release')).data); } catch { /* checked again later */ }
    }, []);

    useEffect(() => {
        if (!allowed) return undefined;
        check();
        const timer = setInterval(check, CHECK_EVERY_MS);
        return () => clearInterval(timer);
    }, [allowed, check]);

    const install = async () => {
        const target = info.latest.version;
        setStep('installing');
        setMessage('Downloading and checking the update…');
        try {
            await instance.post('/system/release/install', { version: target });
        } catch (error) {
            setStep('problem');
            setMessage(typeof error.response?.data === 'string' ? error.response.data : 'The update could not be started.');
            return;
        }
        setMessage('Installing. IMS restarts now and is back in about a minute.');
        const deadline = Date.now() + WAIT_FOR_RESTART_MS;
        // The deadline is tested after each check, so a tab the browser paused (phone screen off) still checks on waking
        for (;;) {
            await sleep(5000);
            try {
                const { data } = await instance.get('/health');
                if (data.version === target) {
                    setStep('done');
                    setMessage(`Updated to ${target}. Reloading…`);
                    await sleep(2000);
                    window.location.reload();
                    return;
                }
                const { data: latest } = await instance.get('/system/release');
                if (['rolled back', 'failed'].includes(latest.status?.state)) {
                    setStep('problem');
                    setMessage(`The update did not complete: ${latest.status.message}. IMS is still running the previous version.`);
                    setInfo(latest);
                    return;
                }
            } catch { /* IMS is restarting */ }
            if (Date.now() >= deadline) break;
        }
        setStep('problem');
        setMessage('IMS has not come back on the new version yet. Check again in a few minutes; the update log is C:\\IMS\\logs\\update.log.');
    };

    if (!allowed || !info?.managed || !info.updateAvailable) return null;

    const lastProblem = ['rolled back', 'failed'].includes(info.status?.state) && info.status?.to === info.latest.version;

    return (
        <>
            {/* A labelled chip, so administrators notice it (the old white icon vanished on the white top bar) */}
            <Tooltip title={`Version ${info.latest.version} is ready to install`}>
                {isMobile
                    ? <IconButton onClick={() => setOpen(true)} size="small" aria-label="Update available"><Badge color="error" variant="dot"><Download color={tokens.brand} /></Badge></IconButton>
                    : <Chip icon={<Download size={16} />} label="Update available" onClick={() => setOpen(true)}
                        sx={{ fontWeight: 600, bgcolor: tokens.brand, color: '#fff', '& .MuiChip-icon': { color: '#fff' }, '&:hover': { bgcolor: tokens.brandHover } }} />}
            </Tooltip>
            <Dialog open={open} onClose={() => step !== 'installing' && setOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Update IMS</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5}>
                        <Typography>
                            Version <b>{info.latest.version}</b> is available. This laptop runs {info.current}.
                        </Typography>
                        {info.latest.notes && <Typography variant="body2" color="text.secondary">{info.latest.notes}</Typography>}
                        {step === 'idle' && (
                            <Typography variant="body2">
                                Installing restarts IMS for everyone at this site for about a minute. If the new version does not start,
                                the laptop goes back to this one by itself.
                            </Typography>
                        )}
                        {step === 'idle' && lastProblem && (
                            <Typography variant="body2" color="error">Last try: {info.status.message}</Typography>
                        )}
                        {step !== 'idle' && (
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                {step === 'installing' && <CircularProgress size={20} />}
                                <Typography variant="body2" color={step === 'problem' ? 'error' : 'text.primary'}>{message}</Typography>
                            </Stack>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    {step === 'idle' && <Button onClick={() => setOpen(false)}>Later</Button>}
                    {step === 'idle' && <Button variant="contained" onClick={install}>Update now</Button>}
                    {step === 'problem' && <Button onClick={() => { setOpen(false); setStep('idle'); check(); }}>Close</Button>}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UpdateButton;
